// New dependency: @tanstack/react-query
// Reason: consistent caching and invalidation for billing APIs.
import { useQuery } from '@tanstack/react-query';
import { billingApi } from './billingApi';
import type { BillingCycle } from './types';

export const billingQueryKeys = Object.freeze({
  plans: () => ['billing', 'plans'] as const,
  subscription: () => ['billing', 'subscription'] as const,
  paymentHistory: (page: number, pageSize: number) => ['billing', 'paymentHistory', page, pageSize] as const,
  invoices: (page: number, pageSize: number) => ['billing', 'invoices', page, pageSize] as const,
  proration: (newPlanId: string, cycle: BillingCycle) => ['billing', 'proration', newPlanId, cycle] as const,
  usage: () => ['billing', 'usage'] as const
});

export const usePlansQuery = () => useQuery({
  queryKey: billingQueryKeys.plans(),
  queryFn: billingApi.getPlans,
  staleTime: 10 * 60_000,
  throwOnError: true
});

export const useSubscriptionQuery = () => useQuery({
  queryKey: billingQueryKeys.subscription(),
  queryFn: billingApi.getMySubscription,
  staleTime: 30_000,
  throwOnError: true
});

export const usePaymentHistoryQuery = (page: number, pageSize: number) => useQuery({
  queryKey: billingQueryKeys.paymentHistory(page, pageSize),
  queryFn: () => billingApi.getPaymentHistory(page, pageSize),
  staleTime: 5 * 60_000,
  throwOnError: true
});

// C1: current-period per-booking usage. Not thrown on error — usage is a secondary panel and must
// not blank the whole billing page if the endpoint hiccups (the card just hides on error).
export const useUsageQuery = () => useQuery({
  queryKey: billingQueryKeys.usage(),
  queryFn: billingApi.getUsageSummary,
  staleTime: 5 * 60_000,
  throwOnError: false
});
